<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import ContactListHeaderWrapper from 'dashboard/components-next/Contacts/ContactsHeader/ContactListHeaderWrapper.vue';
import ContactsActiveFiltersPreview from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactsActiveFiltersPreview.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import ContactsLoadMore from 'dashboard/components-next/Contacts/ContactsLoadMore.vue';

const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  showPaginationFooter: { type: Boolean, default: true },
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 100 },
  itemsPerPage: { type: Number, default: 15 },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
  activeSegment: { type: Object, default: null },
  segmentsId: { type: [String, Number], default: 0 },
  hasAppliedFilters: { type: Boolean, default: false },
  isFetchingList: { type: Boolean, default: false },
  useInfiniteScroll: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
  isLoadingMore: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:currentPage',
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
  'loadMore',
]);

const route = useRoute();

const contactListHeaderWrapper = ref(null);

const isNotSegmentView = computed(() => {
  return route.name !== 'contacts_dashboard_segments_index';
});

const isActiveView = computed(() => {
  return route.name === 'contacts_dashboard_active';
});

const isLabelView = computed(
  () => route.name === 'contacts_dashboard_labels_index'
);

const showActiveFiltersPreview = computed(() => {
  return (
    (props.hasAppliedFilters || !isNotSegmentView.value) &&
    !props.isFetchingList &&
    !isLabelView.value &&
    !isActiveView.value
  );
});

const updateCurrentPage = page => {
  emit('update:currentPage', page);
};

const openFilter = () => {
  contactListHeaderWrapper.value?.onToggleFilters();
};

const showLoadMore = computed(() => {
  return props.useInfiniteScroll && props.hasMore;
});

const showPagination = computed(() => {
  return !props.useInfiniteScroll && props.showPaginationFooter;
});
</script>

<template>
  <section
    :class="[
      'max-md:[&_.contacts-list-toolbar]:!gap-2',
      'max-md:[&_.contacts-list-toolbar]:!overflow-visible',
      'max-md:[&_.contacts-list-toolbar>div]:!min-w-0',
      'max-md:[&_.contacts-list-toolbar>div]:!w-full',
      'max-md:[&_.contacts-list-toolbar>div:first-child]:!grid',
      'max-md:[&_.contacts-list-toolbar>div:first-child]:!grid-cols-3',
      'max-md:[&_.contacts-list-toolbar>div:first-child]:!gap-1',
      'max-md:[&_.contacts-filter-pill]:!h-auto',
      'max-md:[&_.contacts-filter-pill]:!min-h-11',
      'max-md:[&_.contacts-filter-pill]:!justify-center',
      'max-md:[&_.contacts-filter-pill]:!whitespace-normal',
      'max-md:[&_.contacts-filter-pill]:!px-1',
      'max-md:[&_.contacts-filter-pill]:!text-center',
      'max-md:[&_.contacts-filter-pill]:!leading-4',
      'max-md:[&_.acl-mobile-account-menu-host]:!justify-end',
      'max-md:[&_.acl-mobile-account-menu-host]:!gap-2',
      'max-md:[&_[class*=contacts-page-header][class*=team-filter]]:!static',
      'max-md:[&_.contacts-sort-menu]:!static',
      'max-md:[&_[class*=contacts-page-header][class*=team-menu]]:!inset-x-0',
      'max-md:[&_[class*=contacts-page-header][class*=team-menu]]:!w-full',
      'max-md:[&_[class*=contacts-page-header][class*=team-menu]]:!min-w-0',
      'max-md:[&_[class*=contacts-page-header][class*=team-menu]]:!max-h-[min(20rem,50dvh)]',
      'max-md:[&_[class*=contacts-page-header][class*=team-menu]]:!overflow-y-auto',
      'max-md:[&_[class*=contacts-page-header][class*=team-menu]]:!z-[120]',
      'max-md:[&_[class*=contacts-sort-menu][class*=options]]:!inset-x-0',
      'max-md:[&_[class*=contacts-sort-menu][class*=options]]:!w-full',
      'max-md:[&_[class*=contacts-sort-menu][class*=options]]:!min-w-0',
      'max-md:[&_[class*=contacts-sort-menu][class*=options]]:!max-h-[min(20rem,50dvh)]',
      'max-md:[&_[class*=contacts-sort-menu][class*=options]]:!overflow-y-auto',
      'max-md:[&_[class*=contacts-sort-menu][class*=options]]:!z-[120]',
      'max-md:[&_[class*=contacts-page-header][class*=team-option]]:!whitespace-normal',
      'max-md:[&_[class*=contacts-page-header][class*=team-option]]:break-words',
      'max-md:[&_.acl-mobile-account-menu-host_button]:!min-h-11',
      'max-md:[&_.acl-mobile-account-menu-host_button]:!min-w-11',
    ]"
    class="contacts-list-layout flex w-full h-full gap-4 overflow-hidden justify-evenly"
  >
    <div class="flex flex-col w-full h-full transition-all duration-300">
      <ContactListHeaderWrapper
        ref="contactListHeaderWrapper"
        :show-search="isNotSegmentView && !isActiveView"
        :search-value="searchValue"
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        :header-title="headerTitle"
        :active-segment="activeSegment"
        :segments-id="segmentsId"
        :has-applied-filters="hasAppliedFilters"
        :is-label-view="isLabelView"
        :is-active-view="isActiveView"
        @update:sort="emit('update:sort', $event)"
        @search="emit('search', $event)"
        @apply-filter="emit('applyFilter', $event)"
        @clear-filters="emit('clearFilters')"
      />
      <main
        class="contacts-list-layout__body flex-1 overflow-y-auto px-3 sm:px-6"
      >
        <div class="w-full mx-auto max-w-5xl">
          <ContactsActiveFiltersPreview
            v-if="showActiveFiltersPreview"
            :active-segment="activeSegment"
            class="mb-1"
            @clear-filters="emit('clearFilters')"
            @open-filter="openFilter"
          />
          <slot name="default" />
          <ContactsLoadMore
            v-if="showLoadMore"
            :is-loading="isLoadingMore"
            @load-more="emit('loadMore')"
          />
        </div>
      </main>
      <footer v-if="showPagination" class="sticky bottom-0 z-0">
        <PaginationFooter
          current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
          :current-page="currentPage"
          :total-items="totalItems"
          class="max-w-[67rem]"
          :items-per-page="itemsPerPage"
          @update:current-page="updateCurrentPage"
        />
      </footer>
    </div>
  </section>
</template>

<style scoped>
.contacts-list-layout {
  background: #f6f6f6;
}

.contacts-list-layout__body {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.42) transparent;
}

.contacts-list-layout__body::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.contacts-list-layout__body::-webkit-scrollbar-track {
  background: transparent;
}

.contacts-list-layout__body::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.42);
  border-radius: 999px;
}
</style>
